"use client"

import EmptyYet from "@/app/[locale]/_components/Empty"
import { downloadReportPdfUrl, fetchReportsDetailList, getReportPdfUrl } from "@/services/publicationcenter"
import { formatDateEn } from "@repo/shared"
import { useSigninModal } from "@repo/ui/signin"
import classNames from "classnames"
import { TABLE_LIMIT } from "common/helper/const"
import { ReportsDetailItem, SubscribeType } from "common/interface/interface"
import { ReportDetailParams } from "common/interface/params"
import React, { useEffect, useState } from "react"
import { Space, Table, TableProps } from "antd"

import ReportPagination from "../../../_components/Pagination"
import SelectMix from "../../../_components/SelectFilter/mix"
import Style from "./index.module.scss"

async function getReportsDetailList(params: ReportDetailParams) {
	try {
		const data = await fetchReportsDetailList(params)
		return data || { list: [], info: { total: 0 } }
	} catch (err) {
		console.error("[ContentTable] fetchReportsDetailList failed", err)
		return { list: [], info: { total: 0 } }
	}
}

type Props = {
	yearList: string[]
	list: ReportsDetailItem[]
	total: number
	reportId: number
	token: string
}

const ContentTable: React.FC<Props> = ({ yearList, list, total, reportId, token }) => {
	const pageSize = TABLE_LIMIT
	const { showSignInModal } = useSigninModal()
	const [currentPage, setCurrentPage] = useState<number>(1)
	const [totalCount, setTotalCount] = useState<number>(total)
	const [tableData, setTableData] = useState<ReportsDetailItem[]>(list)
	const [selectedYear, setSelectedYear] = useState<string>("0")
	const [subscribeType, setSubscribeType] = useState<SubscribeType>(SubscribeType.All)

	const [isMobile, setIsMobile] = useState(false)

	useEffect(() => {
		const checkIsMobile = () => {
			if (typeof window !== "undefined") {
				setIsMobile(window.innerWidth < 768)
			}
		}

		checkIsMobile()
		window.addEventListener("resize", checkIsMobile)
		return () => {
			window.removeEventListener("resize", checkIsMobile)
		}
	}, [])

	const fetchTableData = async (
		pageParam: number = currentPage,
		yearParam: string = selectedYear,
		subscribeParam: SubscribeType = subscribeType
	) => {
		const { list, info } = await getReportsDetailList({
			id: reportId,
			auth_token: token,
			report_type: subscribeParam,
			report_year: +yearParam,
			page: pageParam,
			limit: pageSize,
		})
		setTotalCount(info.total)
		setTableData(list)
	}

	const handleView = (record: ReportsDetailItem) => {
		if (record.attachment_type === "PDF") {
			window.open(getReportPdfUrl({ pub_child_id: record?.id || 0, auth_token: token }))
		} else {
			window.open(downloadReportPdfUrl({ pub_child_id: record?.id || 0, auth_token: token }))
		}
	}

	const columns: TableProps<ReportsDetailItem>["columns"] = [
		{
			title: "Reports Name",
			dataIndex: "name",
			render: (text, record) => (
				<span className={classNames({ [Style.nopermission]: !token || !record.has_auth })}>{text}</span>
			),
		},
		{
			title: "Update Date",
			dataIndex: "address",
			render: (_, record) => (
				<span className={classNames({ [Style.nopermission]: !token || !record.has_auth })}>
					{formatDateEn(record.updated_time * 1000)}
				</span>
			),
		},
		{
			title: "Operation",
			render: (_, record) => (
				<Space>
					{token ? (
						record.has_auth ? (
							<a onClick={() => handleView(record)}>View</a>
						) : (
							<span className={Style.nopermission}>No permission</span>
						)
					) : (
						<a
							onClick={() =>
								showSignInModal({
									animate: isMobile ? "drawer" : "modal",
									onLoginSuccess: () => {
										window.location.reload()
									},
									onRegisterSuccess: () => {
										window.location.reload()
									},
								})
							}
						>
							Sign in
						</a>
					)}
				</Space>
			),
		},
	]

	const handleChange = (page: number) => {
		setCurrentPage(page)
		fetchTableData(page)

		// DOM 更新后再滚动到 tab 位置
		requestAnimationFrame(() => {
			const anchorElement = document.getElementById("report-table-anchor")
			if (anchorElement !== null) {
				const yOffset = -120 // 向上偏移 120px
				const y = anchorElement.getBoundingClientRect().top + window.pageYOffset + yOffset
				window.scrollTo({ top: y, behavior: "smooth" })
			}
		})
	}

	return (
		<div className={Style.contentTable}>
			<div className={Style.header} id="report-table-anchor">
				<h3>Reports List</h3>
				<div className={Style.action}>
					<SelectMix
						className={Style.subscribe}
						value={subscribeType}
						options={[
							{ label: "All", value: SubscribeType.All },
							{ label: "Subscribed", value: SubscribeType.Subscribe },
							{ label: "Unsubscribed", value: SubscribeType.Unsubscribe },
						]}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
						onChange={(val) => {
							setSubscribeType(val)
							const nextPage = 1
							if (currentPage !== nextPage) {
								setCurrentPage(nextPage)
							}
							fetchTableData(nextPage, selectedYear, val)
						}}
					/>

					<SelectMix
						className={Style.years}
						value={selectedYear}
						options={["All Years", ...yearList].map((item) => ({
							label: item,
							value: item === "All Years" ? "0" : item,
						}))}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
						onChange={(val) => {
							setSelectedYear(val)
							const nextPage = 1
							if (currentPage !== nextPage) {
								setCurrentPage(nextPage)
							}
							fetchTableData(nextPage, val, subscribeType)
						}}
					/>
				</div>
			</div>

			<Table<ReportsDetailItem>
				columns={columns}
				dataSource={tableData}
				pagination={false}
				rowKey={"id"}
				className={Style.table}
				bordered={false}
				locale={{
					emptyText: <EmptyYet />,
				}}
			/>

			<ReportPagination
				showTotal
				total={totalCount}
				hideOnSinglePage={false}
				currentPage={currentPage}
				pageSize={pageSize}
				onChange={handleChange}
			/>
		</div>
	)
}

export default ContentTable
